/**
 * How a member's card on file is DISPLAYED — the three derivations shared by the
 * Payment methods tab (DNKADM-0252) and the point-of-sale card-on-file chooser
 * (DNKADM-0253). They live here so the two surfaces cannot drift: an expired card must
 * read the same at the counter as it does on the tab.
 *
 * `cardId` is **OPAQUE** and appears in none of these — a card is identified to a human
 * by brand + masked pan only. `cardBrand`/`maskedPan` are backend text and are
 * `scrubBrand`-ed here, at the render boundary that produces the label.
 */
import { scrubBrand } from '@/utils/whitelabel';
import { dedupeCardBrand } from '@/utils/cardBrand';
import type { MemberCard } from '@/api/memberCards';

const PLACEHOLDER = '—';

/**
 * The card's ONE derived display name — brand + masked pan, each brand-scrubbed and
 * each degrading to {@link PLACEHOLDER} on its own, so a partly-known card never reads
 * as a missing one. Every visible label and accessible name comes from this single
 * value, so they can never drift apart.
 *
 * DNKADM-0355: this prepend is where the doubling is MADE for these two surfaces —
 * pay-api vaults some cards with the brand already inside `maskedPan`, and prepending
 * `cardBrand` unconditionally on top of that rendered "VISA VISA **** 1111". The
 * composition stays (a card whose `maskedPan` is clean still needs its brand named, and
 * that is the shape pay-api is moving to) but it is no longer unconditional:
 * {@link dedupeCardBrand} collapses the repeat, so the brand renders exactly once
 * whichever shape the wire sends.
 */
export function cardLabel(card: MemberCard): string {
  const brand = scrubBrand(card.cardBrand);
  const pan = scrubBrand(card.maskedPan);
  // Only a real brand next to a real pan can double. When either is missing the
  // placeholder stands in and the label is returned undeduped on purpose: `— —` is a
  // card known by neither field, and it must not collapse into a single `—` that would
  // read as one known value.
  if (brand === null || pan === null) return `${brand ?? PLACEHOLDER} ${pan ?? PLACEHOLDER}`;
  return dedupeCardBrand(`${brand} ${pan}`);
}

/** `MM/YYYY`, or the placeholder when the server carries no usable expiry. */
export function expiryLabel(card: MemberCard): string {
  if (card.expMonth === null || card.expYear === null) return PLACEHOLDER;
  return `${String(Math.trunc(card.expMonth)).padStart(2, '0')}/${Math.trunc(card.expYear)}`;
}

/**
 * Is this card past its expiry? Inferred CLIENT-side (the server deliberately returns
 * expired cards). A card missing either field is **not** expired — a missing value is
 * never evidence, and wrongly striking a good card would push the desk to delete it.
 */
export function isCardExpired(card: MemberCard, now: Date): boolean {
  if (card.expMonth === null || card.expYear === null) return false;
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  if (card.expYear < year) return true;
  return card.expYear === year && card.expMonth < month;
}
